import { useFetchNowSessions } from "@multicines/services";
import { useFetchFilteredMovies } from "@multicines/services";
import { useMoviesStore } from "@multicines/stores";
import { Movie } from "@multicines/types";
import { getApiMessageError, getTradeCodeApiError } from "@multicines/utils";
import { Button, ScrollableTabs } from "artisn-ui-react";
import dayjs from "dayjs";
import EmptyBillboardSVG from "images/empty-billboard.svg";
import FiltersSVG from "images/filters.svg";
import LoadingSVG from "images/loading.svg";
import Lottie from "lottie-react-web";
import React, { useMemo, useState } from "react";

import { getTabs, getTabName } from "./Billboard.helpers";
import Styles from "./Billboard.styles";
import { BillboardProps as Props, MoviePartial } from "./Billboard.types";
import BillboardFilters from "./BillboardFilters/BillboardFilters";
import BillboardPlaceholder from "./BillboardPlaceholder/BillboardPlaceholder";
import NowFilter from "./NowFilter/NowFilter";
import NowFilterPlaceholder from "./NowFilter/NowFilterPlaceholder/NowFilterPlaceholder";
import PreviewFilter from "./PreviewFilter/PreviewFilter";
import useBillboardAnalytics from "./useBillboardAnalytics";
import ChangeStore from "components/Cart/ChangeStore/ChangeStore";
import CinemaAndDay from "components/CinemaAndDay/CinemaAndDay";
import EmptyState from "components/global/EmptyState/EmptyState";
import ErrorState from "components/global/ErrorState/ErrorState";
import Footer from "components/global/Footer/Footer";
import MovieCard from "components/global/MovieCard/MovieCard";
import Navbar from "components/global/Navbar/Navbar";
import UserData from "components/global/UserData/UserData";
import useI18n from "hooks/useI18n";
import useTalkShop from "hooks/useTalkShop";
import { useAuthStore } from "stores/auth/auth.store";
import { useGlobalsStore } from "stores/globals/globals.store";
import { useStoresStore } from "stores/stores/stores.store";

import AnimationJSON from "../../../public/assets/animations/colors.json";

const empty = -999;

const Billboard: React.FC<Props> = props => {
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const { isTalkShop } = useTalkShop();
  const selectedStore = useStoresStore(store => store.selectedStore);
  const setSelectedMovie = useMoviesStore(store => store.setSelectedMovie);
  const globalDate = useMoviesStore(store => store.globalDate);
  const t = useI18n();
  const modalityFilter = useGlobalsStore(state => state.modalityFilter);
  const languageFilter = useGlobalsStore(state => state.languageFilter);
  const formatFilter = useGlobalsStore(state => state.formatFilter);
  const theaterFilter = useGlobalsStore(state => state.theaterFilter);
  const setModalityFilter = useGlobalsStore(state => state.setModalityFilter);
  const filteredQuery = useFetchFilteredMovies(
    { isAnonymous, uid },
    modalityFilter,
    selectedStore
  );
  const fetchNowSessions = useFetchNowSessions(
    { isAnonymous, uid },
    selectedStore,
    globalDate
  );

  const [opened, setOpened] = useState(false);

  const { data: moviesRaw, status, error, refetch } = filteredQuery;
  const { isSuccess, isFetched } = filteredQuery;
  useBillboardAnalytics({
    uid,
    isSuccessShowBillboard: isSuccess,
    isFetchedShowBillboard: isFetched,
    movies: moviesRaw
  });

  const { data: nowSessions, isFetching } = fetchNowSessions;
  const { refetch: nowRefetch } = fetchNowSessions;
  const { status: nowStatus, error: nowError } = fetchNowSessions;

  const isError = status === "error";
  const noMovies = !!moviesRaw && moviesRaw.length === 0;
  const isPremiere = modalityFilter === "Premiere";
  const isPresale = modalityFilter === "Presale";
  const isPreview = modalityFilter === "Preview";
  const allFilter = modalityFilter === "All";

  const movies: MoviePartial[] | undefined = useMemo(() => {
    if (!moviesRaw) return undefined;
    if (moviesRaw.length % 2 === 0) return moviesRaw;
    return [
      ...moviesRaw,
      {
        id: empty,
        modality: null,
        languages: [],
        theater_types: [],
        format_types: [],
        openingDate: ""
      }
    ];
  }, [moviesRaw]);

  const refetchMoviesQuery = async () => {
    await refetch();
  };

  const renderPremiere = (
    <>
      {nowStatus === "idle" || nowStatus === "loading" ? (
        <NowFilterPlaceholder />
      ) : null}
      {nowStatus === "error" ? (
        <ErrorState
          title={getTradeCodeApiError(nowError, "TMSN", true)}
          description={getApiMessageError(nowError)}
          onClick={nowRefetch}
        />
      ) : null}
      {nowStatus === "success"
        ? nowSessions?.map((movie, index) => {
            return (
              <NowFilter
                key={index}
                movie={movie as Movie}
                sessionTypes={nowSessions}
                isFetching={isFetching}
              />
            );
          })
        : null}
    </>
  );

  const groupedMovies = useMemo(() => {
    const orderedMovies = movies
      ?.filter(movie => movie.id !== empty)
      .sort((a, b) =>
        dayjs(a.openingDate).isBefore(dayjs(b.openingDate)) ? -1 : 1
      );

    const groups = {} as Movie | any;
    orderedMovies?.forEach(movie => {
      const dateKey = dayjs(movie.openingDate).format("YYYY-MM");
      if (groups[dateKey]) {
        groups[dateKey].push(movie);
      } else {
        groups[dateKey] = new Array(movie);
      }
    });

    const sortedKeys = Object.keys(groups).sort((a, b) =>
      dayjs(a).isBefore(dayjs(b)) ? -1 : 1
    );
    const values: MoviePartial[][] = [];
    sortedKeys.forEach(key => {
      values.push(groups[key]);
    });

    return values;
  }, [movies]);

  const renderPresale = () => {
    if (status === "idle" || status === "loading") {
      return (
        <div className="Billboard__content Billboard__content--loading">
          <LoadingSVG />
        </div>
      );
    }
  };

  const renderPreview = () => {
    if (status === "idle" || status === "loading") {
      return (
        <div className="Billboard__content Billboard__content--loading">
          <LoadingSVG />
        </div>
      );
    }

    if (status === "success") {
      return <PreviewFilter movie={groupedMovies} />;
    }
  };

  const renderBillboard = () => {
    return movies
      ?.filter(item => item.modality === modalityFilter)
      .map((movie, index) => {
        return renderItem(movie, index);
      });
  };

  const renderItem = (listItem: MoviePartial, index: number) => {
    const movie = listItem;
    if (movie.id === empty) {
      return <div key={index} />;
    }
    const pressHandler = () => setSelectedMovie(movie as Movie);
    return (
      <MovieCard key={index} movie={movie as Movie} onClick={pressHandler} />
    );
  };

  const renderStates = (
    <>
      {status === "loading" || status === "idle" ? (
        <BillboardPlaceholder className="Billboard__placeholder" />
      ) : null}

      {isError ? (
        <ErrorState
          title={getTradeCodeApiError(error, "TM", true)}
          description={getApiMessageError(error)}
          onClick={refetchMoviesQuery}
        />
      ) : null}

      {status === "success" && noMovies ? (
        <div className="Billboard__empty__wrapper">
          <EmptyState
            icon={<EmptyBillboardSVG />}
            title={t.movie.emptyBillboard}
            description={t.movie.emptyBillboardMessage}
          />
          <ChangeStore
            buttonStyle="Billboard__empty"
            textButton={t.common.changeCinema}
          />
        </div>
      ) : null}

      {status === "success" && !noMovies
        ? movies
            ?.filter(item =>
              modalityFilter === "All"
                ? item
                : item?.modality === modalityFilter
            )
            ?.filter(item =>
              languageFilter === "All"
                ? item
                : item?.languages?.[0] === languageFilter
            )
            ?.filter(item =>
              theaterFilter === "All"
                ? item
                : item?.theater_types?.[0] === theaterFilter
            )
            ?.filter(item =>
              formatFilter === "All"
                ? item
                : item?.format_types?.[0] === formatFilter
            )
            .map((movie, index) => {
              return renderItem(movie, index);
            })
        : null}
    </>
  );

  return (
    <Styles
      className="Billboard"
      noMovies={noMovies || isError}
      isPremiere={modalityFilter === "Premiere"}
      isPreview={modalityFilter === "Preview"}
      talkShop={isTalkShop}
    >
      <div className="bubbles-animation">
        <Lottie
          options={{
            animationData: AnimationJSON,
            autoplay: true,
            loop: true
          }}
        />
      </div>
      <header className="Billboard__header">
        <Navbar showBrand />
      </header>
      <main className="Billboard__main">
        <div className="Billboard__cinema">
          <CinemaAndDay />
        </div>
        <UserData className="Billboard__main-header" isProfile={false} />
        <div className="Billboard__tabs">
          <ScrollableTabs
            defaultActiveItem={1}
            activeClassName="Billboard__tabs--active"
          >
            <div
              className="Billboard__button Billboard__filter"
              onClick={() => setOpened(true)}
            >
              <FiltersSVG className="Billboard__button__icon" />
              {/* {t.filters.filter} */}
            </div>
            {getTabs().map((item, index) => {
              const styled =
                modalityFilter === item ? "Billboard__active" : undefined;
              return (
                <div
                  className="Billboard__scrollable"
                  key={index}
                  onClick={() => setModalityFilter(item)}
                >
                  <Button className="Billboard__button Billboard__menu">
                    {getTabName(item, t.filters)}
                    <div className={`${styled}`} />
                  </Button>
                </div>
              );
            })}
          </ScrollableTabs>
        </div>
        <BillboardFilters opened={opened} setOpened={setOpened} />
        <div className="Billboard__main__movies">
          {allFilter ? renderStates : null}
          {isPremiere ? renderPremiere : null}
          {isPresale ? renderPresale() : null}
          {isPreview ? renderPreview() : null}
          {!allFilter && !isPremiere && !isPreview ? renderBillboard() : null}
        </div>
      </main>
      <footer className="Billboard__footer">
        <Footer />
      </footer>
    </Styles>
  );
};

export default Billboard;
